import WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import Button from '@alltrails/denali/components/Button';
import Link from '@alltrails/denali/components/Link';
import { Typography, executeRecaptcha, NoSsr, getCopyrightText } from '@alltrails/core';
import { useState, ReactNode, ComponentProps, useRef, Suspense, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type CurrencyCode from '@alltrails/core/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import classNames from 'classnames';
import type CountryCode from '@alltrails/core/types/CountryCode';
import { ApiError } from '@alltrails/core/utils/api';
import type { RecurlyError, TokenPayload, ApplePayConfig } from '@recurly/recurly-js';
import { Elements, RecurlyProvider, useRecurly } from '@recurly/react-recurly';
import logPurchaseFailed from '@alltrails/amplitude/events/logPurchaseFailed';
import SectionDivider from '@alltrails/denali/components/SectionDivider';
import type Coupon from '@alltrails/types/Coupon';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useExposureEvent } from '@alltrails/experiments';
import { useSelector } from 'react-redux';
import type { userAgent } from 'next/server';
import logPurchaseButtonTapped from '@alltrails/amplitude/events/logPurchaseButtonTapped';
import getPurchaseScreenTypePlan from '../../utils/getPurchaseScreenTypePlan';
import useTokenizedFormValidation from '../TokenizedCreditCardForm/hooks/useTokenizedFormValidation';
import type ProResponse from '../../types/ProResponse';
import ComplianceCheckbox from '../ComplianceCheckbox/ComplianceCheckbox';
import useIosWebCheckoutExperiment from '../../hooks/useIosWebCheckoutExperiment';
import CheckoutModalFooter from './CheckoutModalFooter';
import CouponCode from './CouponCode';
import styles from './Checkout.module.scss';
import PaymentForm from './PaymentForm';
import RadioPaymentForm from './RadioPaymentForm/RadioPaymentForm';
import { PaymentOptionValues } from './RadioPaymentForm/PaymentOptions';
import RadioPaymentButtons from './RadioPaymentButtons';

// Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c1bcf26ba802db57c180b
// Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c241f824169281f996dc1

type Props = {
  applePayConfig: ApplePayConfig;
  billedHeading?: ReactNode;
  billedSubheading?: ReactNode;
  billedSubheadingClassName?: string;
  billedSubheadingRight?: ReactNode;
  billedToday: ReactNode;
  billingCountry: CountryCode;
  buttonText: ReactNode;
  className?: string;
  coupon?: Coupon;
  couponError?: string;
  currencyCode: CurrencyCode;
  finePrint?: ReactNode;
  heading: ReactNode;
  headingClassName?: string;
  isComplianceCheckboxEnabled?: boolean;
  isUpgrade?: boolean;
  isGooglePayAvailable?: boolean;
  onApplePayToken: ComponentProps<typeof PaymentForm>['onApplePayToken'];
  onBillingCountryChange: (countryCode: CountryCode, response: any) => void;
  onGooglePayToken: ComponentProps<typeof PaymentForm>['onGooglePayToken'];
  onPayPalToken: ComponentProps<typeof PaymentForm>['onPayPalToken'];
  onSubmitTokenizedCardForm: (payload: { cc_token?: string; recaptcha: string }) => void;
  plan: Plan;
  promoText?: ReactNode;
  setCoupon?: (arg0: Coupon) => void;
  setPlanToAnnual?: () => void;
  subheading?: ReactNode;
  webProPurchaseLocation: WebProPurchaseLocation;
  purchaseTier: PurchaseTier;
} & ({ isCheckoutModal: true; features?: never; trigger: UpsellTrigger } | { isCheckoutModal?: never; features: ReactNode; trigger?: never });

function CheckoutBase({
  applePayConfig,
  buttonText,
  billedHeading,
  billedToday,
  billedSubheading,
  billedSubheadingClassName,
  billingCountry,
  className,
  coupon,
  couponError,
  currencyCode,
  finePrint,
  promoText,
  heading,
  headingClassName,
  isComplianceCheckboxEnabled = false,
  isCheckoutModal,
  isUpgrade,
  isGooglePayAvailable = false,
  onApplePayToken,
  onGooglePayToken,
  onPayPalToken,
  plan,
  subheading,
  billedSubheadingRight,
  setCoupon,
  setPlanToAnnual,
  webProPurchaseLocation,
  features,
  onBillingCountryChange,
  onSubmitTokenizedCardForm,
  purchaseTier,
  trigger
}: Props) {
  const { isIosWebCheckoutOptimizationEnabled, isIosNativeApp } = useIosWebCheckoutExperiment();
  const reactRecurlyInstance = useRecurly();
  const intl = useIntl();

  const browserName = useSelector((state: { browser?: ReturnType<typeof userAgent>['browser'] }) => state.browser?.name);
  const isChrome = browserName?.includes('Chrome');

  const isRadioUiEnabled = !isChrome;
  const sendApplePaySdkExposure = useExposureEvent('web-growth-rm-apple-pay-sdk');

  const [submittingCreditCard, setSubmittingCreditCard] = useState(false);
  const isMobile = useIsMobileSizedScreen();
  const isFinePrintLeftAligned = billingCountry === 'US' && !isCheckoutModal && !isMobile && !isComplianceCheckboxEnabled;

  // non-credit card submitting that controls the overlay
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState<ProResponse['errors']>();
  const formRef = useRef<HTMLFormElement>(null);
  const submitButtonContainerRef = useRef<HTMLDivElement>(null);
  const { validationErrors, setClientValidationErrors, setTokenRequestErrors, hasValidationErrors } = useTokenizedFormValidation();

  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentOptionValues>();

  const [isComplianceChecked, setIsComplianceChecked] = useState(false);
  const [complianceError, setComplianceError] = useState(false);

  const [submitButtonHeight, setSubmitButtonHeight] = useState(0);

  // If the iOS web checkout optimization is enabled, the submit button
  //  is fixed to the bottom of the page for all treatment variants.
  const [isButtonFixed, setIsButtonFixed] = useState(isIosWebCheckoutOptimizationEnabled ? true : false);

  const sendIosWebCheckoutOptimizationExposure = useExposureEvent('web-growth-rm-mobile-to-web-checkout-optimization');

  useEffect(() => {
    sendApplePaySdkExposure();
  }, [sendApplePaySdkExposure]);

  useEffect(() => {
    if (isMobile && isIosNativeApp) {
      sendIosWebCheckoutOptimizationExposure();
    }
  }, [isMobile, isIosNativeApp, sendIosWebCheckoutOptimizationExposure]);

  // Makes the submit button fixed to the bottom of the page until it's original position is scrolled to.
  // "position: sticky" isn't used since the button container needs to extend beyond its container.
  useEffect(() => {
    if (!isIosWebCheckoutOptimizationEnabled) {
      if (isMobile && isComplianceCheckboxEnabled && submitButtonContainerRef.current) {
        const buttonHeight = submitButtonContainerRef.current?.offsetHeight;
        setSubmitButtonHeight(buttonHeight);

        const bottomRootMargin = buttonHeight + 16; // button height + 16px of padding
        const observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (!entry.isIntersecting) {
                setIsButtonFixed(true);
              } else {
                setIsButtonFixed(false);
              }
            });
          },
          {
            rootMargin: `0px 0px -${bottomRootMargin}px 0px`
          }
        );

        if (submitButtonContainerRef.current) {
          observer.observe(submitButtonContainerRef.current);
        }
      }
    }
  }, [isMobile, isComplianceCheckboxEnabled, isIosWebCheckoutOptimizationEnabled]);

  const handleTokenizedCardSubmit = async (e: React.KeyboardEvent | React.FormEvent) => {
    e.preventDefault();

    const isGiftCard = coupon?.type === 'GiftCard';

    if (submittingCreditCard) {
      return;
    }

    if (complianceError) {
      setComplianceError(false);
    }

    const hasComplianceError = isComplianceCheckboxEnabled && !isComplianceChecked;

    if (hasComplianceError) {
      setComplianceError(true);
      logPurchaseFailed({ error_code: 'terms and conditions unchecked', payment_method: 'credit_card' });
    }

    // Skip validation for gift cards.
    if ((hasValidationErrors() || hasComplianceError) && !isGiftCard) {
      return;
    }

    const onRecurlyToken = async (err: RecurlyError | null, token: TokenPayload, recaptcha: string) => {
      if (err) {
        logPurchaseFailed({ error_code: err.message, payment_method: 'credit_card' });
        setTokenRequestErrors(err);
        setSubmittingCreditCard(false);
      } else {
        const body = {
          recaptcha,
          cc_token: token.id
        };

        try {
          await onSubmitTokenizedCardForm(body);
        } catch (e) {
          logPurchaseFailed({
            ...(e instanceof ApiError ? { error_code: e.data.base[0] } : {}),
            payment_method: 'credit_card'
          });

          if (e instanceof ApiError) {
            setErrors(e.data);
          }

          setSubmittingCreditCard(false);
        }
      }
    };

    let recaptcha; // : string | null = null;

    try {
      setSubmittingCreditCard(true);
      recaptcha = await executeRecaptcha('userPaymentAction');

      if (isGiftCard) {
        await onSubmitTokenizedCardForm({ recaptcha });
        setSubmittingCreditCard(false);
        return;
      }

      if (recaptcha) {
        reactRecurlyInstance.token(formRef.current!, (err, token) => onRecurlyToken(err, token, recaptcha));
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setErrors(e.data);
      }
      // else {
      //   setErrors(e);
      // }
      setSubmittingCreditCard(false);
    }
  };

  return (
    <form
      ref={formRef}
      className={classNames(styles.content, className, {
        [styles.checkoutModalFooterPadding]: isCheckoutModal,
        [styles.complianceCheckboxEnabled]: isComplianceCheckboxEnabled,
        [styles.iosWebCheckoutOptimizationEnabled]: isIosWebCheckoutOptimizationEnabled
      })}
      style={{
        paddingBottom: isMobile && isComplianceCheckboxEnabled && isButtonFixed ? `${submitButtonHeight}px` : undefined
      }}
      onSubmit={(e: React.KeyboardEvent | React.FormEvent) => {
        handleTokenizedCardSubmit(e);
      }}
    >
      <div className={styles.columnContainer}>
        <div className={styles.tryArea}>
          <h1
            className={classNames(styles.heading, headingClassName, {
              [styles.complianceHeading]: isComplianceCheckboxEnabled
            })}
          >
            {heading}
          </h1>
          {isRadioUiEnabled && <SectionDivider classes={styles.headingDivider} />}
          {subheading && (
            <div className={styles.subheading}>
              <Typography variant="text100">{subheading}</Typography>
            </div>
          )}
          {features}
        </div>

        <div className={styles.billedCopyright}>
          <div className={styles.billedArea}>
            <div className={styles.headings}>
              <div className={styles.billed}>
                <Typography component="div" variant="text200bold">
                  {billedHeading || <FormattedMessage defaultMessage="Billed today" />}
                </Typography>
                <Typography component="div" variant="text200">
                  {billedToday}
                </Typography>
              </div>
              <div className={styles.hr} />
              {billedSubheading && (
                <div className={classNames(styles.after, billedSubheadingClassName)}>
                  <Typography component="div" variant="text200bold">
                    {billedSubheading}
                  </Typography>
                  <Typography component="div" variant="text200">
                    {billedSubheadingRight}
                  </Typography>
                </div>
              )}
            </div>
            {/* Hide coupon code if any treatment variant is enabled for the iOS web checkout optimization experiment */}
            {setCoupon && setPlanToAnnual && !isIosWebCheckoutOptimizationEnabled && (
              <div>
                <CouponCode
                  couponError={couponError}
                  coupon={coupon}
                  purchaseTier={purchaseTier}
                  setCoupon={setCoupon}
                  setPlanToAnnual={setPlanToAnnual}
                />
              </div>
            )}
          </div>
          {finePrint && isFinePrintLeftAligned && <div className={classNames(styles.fine, styles.usVariant, styles.leftAlign)}>{finePrint}</div>}
          {isComplianceCheckboxEnabled && (
            <ComplianceCheckbox
              finePrint={finePrint}
              checked={isComplianceChecked}
              onChange={checked => {
                setIsComplianceChecked(checked);
                setComplianceError(false);
              }}
              error={complianceError}
            />
          )}
          <div
            className={classNames(styles.copyright, {
              [styles.leftAlign]: isFinePrintLeftAligned,
              [styles.complianceVariant]: isComplianceCheckboxEnabled
            })}
          >
            {getCopyrightText({ intl })}
            <br />
            <div>
              <Link className={styles.link} href="/privacy" target="_blank" noUnderline>
                <FormattedMessage defaultMessage="Privacy Policy" />
              </Link>{' '}
              |{' '}
              <Link className={styles.link} href="/terms" target="_blank" noUnderline>
                <FormattedMessage defaultMessage="Terms of Service" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.columnContainer}>
        <div
          className={classNames(styles.form, {
            [styles.radioFormContainer]: isRadioUiEnabled,
            [styles.complianceEnabled]: isComplianceCheckboxEnabled
          })}
        >
          {isRadioUiEnabled ? (
            <RadioPaymentForm
              billingCountry={billingCountry}
              coupon={coupon}
              currencyCode={currencyCode}
              errors={errors}
              isGooglePayAvailable={isGooglePayAvailable}
              isUpgrade={isUpgrade}
              onApplePayToken={onApplePayToken}
              onBillingCountryChange={onBillingCountryChange}
              onGooglePayToken={onGooglePayToken}
              onPayPalToken={onPayPalToken}
              plan={plan}
              purchaseTier={purchaseTier}
              selectedPaymentType={selectedPaymentType}
              setClientValidationErrors={setClientValidationErrors}
              setErrors={setErrors}
              setSelectedPaymentType={setSelectedPaymentType}
              submitting={submitting}
              validationErrors={validationErrors}
            />
          ) : (
            <PaymentForm
              applePayConfig={applePayConfig}
              billingCountry={billingCountry}
              browserName={browserName}
              coupon={coupon}
              currencyCode={currencyCode}
              errors={errors}
              isComplianceCheckboxEnabled={isComplianceCheckboxEnabled}
              isUpgrade={isUpgrade}
              onBillingCountryChange={onBillingCountryChange}
              onApplePayToken={onApplePayToken}
              onGooglePayToken={onGooglePayToken}
              onPayPalToken={onPayPalToken}
              plan={plan}
              purchaseTier={purchaseTier}
              setClientValidationErrors={setClientValidationErrors}
              setErrors={setErrors}
              validationErrors={validationErrors}
              webProPurchaseLocation={webProPurchaseLocation}
            />
          )}
          {isCheckoutModal && (
            <CheckoutModalFooter
              applePayConfig={applePayConfig}
              coupon={coupon}
              currencyCode={currencyCode}
              isBaseVariant={!isRadioUiEnabled}
              onApplePayToken={onApplePayToken}
              onGooglePayToken={onGooglePayToken}
              onPayPalToken={onPayPalToken}
              plan={plan}
              purchaseTier={purchaseTier}
              selectedPaymentType={selectedPaymentType}
              setErrors={setErrors}
              setSubmitting={setSubmitting}
              submitting={submitting}
              submittingCreditCard={submittingCreditCard}
              trigger={trigger}
              webProPurchaseLocation="modal"
            />
          )}
        </div>
        {!isCheckoutModal && (
          <div ref={submitButtonContainerRef}>
            <div className={classNames(styles.buttonContainer, { [styles.fixedButtonContainer]: isButtonFixed })}>
              {isRadioUiEnabled ? (
                <RadioPaymentButtons
                  applePayConfig={applePayConfig}
                  buttonClassName={styles.button}
                  creditCardButtonText={isCheckoutModal ? <FormattedMessage defaultMessage="Continue" /> : buttonText}
                  coupon={coupon}
                  currencyCode={currencyCode}
                  onApplePayToken={onApplePayToken}
                  onGooglePayToken={onGooglePayToken}
                  onPayPalToken={onPayPalToken}
                  plan={plan}
                  purchaseTier={purchaseTier}
                  selectedPaymentType={selectedPaymentType}
                  setErrors={setErrors}
                  setSubmitting={setSubmitting}
                  submitting={submitting}
                  submittingCreditCard={submittingCreditCard}
                  trigger={trigger}
                  webProPurchaseLocation={webProPurchaseLocation}
                />
              ) : (
                <Button
                  className={styles.button}
                  loading={submittingCreditCard}
                  onClick={() => {
                    logPurchaseButtonTapped({
                      payment_method: 'credit_card',
                      product_type: purchaseTier === 'peak' ? 'peak' : 'plus',
                      purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
                      web_purchase_location: webProPurchaseLocation
                    });
                  }}
                  testId="submit"
                  text={buttonText}
                  type="submit"
                  variant="primary"
                />
              )}
            </div>
          </div>
        )}
        {finePrint && !isFinePrintLeftAligned && !isComplianceCheckboxEnabled && <div className={styles.fine}>{finePrint}</div>}
        {promoText && <div className={styles.promoText}>{promoText}</div>}
      </div>
    </form>
  );
}

const CheckoutBaseProvider = (props: Props) => (
  <NoSsr>
    {/* Note: RecurlyProvider isn't a Suspense-enabled component, so Suspense does nothing and "fallback" isn't rendered. It can be removed */}
    <Suspense fallback={null}>
      {/* RecurlyProvider can't be server rendered */}
      <RecurlyProvider publicKey={process.env.NEXT_PUBLIC_RECURLY_KEY}>
        <Elements>
          <CheckoutBase {...props} />
        </Elements>
      </RecurlyProvider>
    </Suspense>
  </NoSsr>
);

export default CheckoutBaseProvider;
